import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';

import { CompanyOffice } from '../classes/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class CompanyOfficeService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  getCompanyOffices(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<CompanyOffice[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyOffice/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<CompanyOffice[]>(
              `/api/v2/admin/company/companyOffice/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getCompanyOfficeCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyOffice/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/company/companyOffice/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveCompanyOffice(CompanyOffice: CompanyOffice, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post<CompanyOffice>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyOffice`,
              CompanyOffice,
              { params: query }
            ),
          Admin: () =>
            this.http.post<CompanyOffice>(
              `/api/v2/admin/company/companyOffice`,
              CompanyOffice,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteCompanyOffice(CompanyOfficeId: number, query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.delete<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/companyOffice/${CompanyOfficeId}`,
              { params: query }
            ),
          Admin: () =>
            this.http.delete<number>(
              `/api/v2/admin/company/companyOffice/${CompanyOfficeId}`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
